import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import FeatherIcon from 'feather-icons-react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { formatRut } from '../../../utils/formatter';
import ScfoButton from '../ScfoButton';
import {
  getModuleSearchConfig,
  hasModuleSearchFilter,
  stripModuleSearchFields,
  syncModuleSearchFieldsToUrl,
  buildVariablesFromPanelInput,
  mergeInferredIntoVariables,
} from './moduleSearchConfigs';
import { sanitizeFilterAmountInput } from '../../../utils/amountInput';
import './ModuleSearchBar.scss';

const DEBOUNCE_MS = 600;

const TransactionsSearchPanel = ({
  idPrefix,
  searchInputValues,
  variables,
  onPanelFieldChange,
  panelAmountValue,
}) => (
  <>
    <div className="module-search-bar-panel__row module-search-bar-panel__row--2">
      <div className="module-search-bar-field">
        <label className="module-search-bar-label" htmlFor={`${idPrefix}-min-amount`}>
          MONTO MÍNIMO
        </label>
        <div className="module-search-bar-amount-wrap">
          <span className="module-search-bar-amount-prefix" aria-hidden="true">$</span>
          <input
            id={`${idPrefix}-min-amount`}
            type="text"
            placeholder="Monto mínimo..."
            inputMode="decimal"
            value={panelAmountValue('minAmount')}
            onChange={(e) => onPanelFieldChange('minAmount', sanitizeFilterAmountInput(e.target.value))}
          />
        </div>
      </div>
      <div className="module-search-bar-field">
        <label className="module-search-bar-label" htmlFor={`${idPrefix}-max-amount`}>
          MONTO MÁXIMO
        </label>
        <div className="module-search-bar-amount-wrap">
          <span className="module-search-bar-amount-prefix" aria-hidden="true">$</span>
          <input
            id={`${idPrefix}-max-amount`}
            type="text"
            placeholder="Monto máximo..."
            inputMode="decimal"
            value={panelAmountValue('maxAmount')}
            onChange={(e) => onPanelFieldChange('maxAmount', sanitizeFilterAmountInput(e.target.value))}
          />
        </div>
      </div>
    </div>
    <div className="module-search-bar-field">
      <label className="module-search-bar-label" htmlFor={`${idPrefix}-rut`}>RUT</label>
      <input
        id={`${idPrefix}-rut`}
        type="text"
        placeholder="RUT..."
        value={searchInputValues.rut ?? variables.rut ?? ''}
        onChange={(e) => onPanelFieldChange('rut', e.target.value)}
      />
    </div>
    <div className="module-search-bar-field">
      <label className="module-search-bar-label" htmlFor={`${idPrefix}-description`}>
        DESCRIPCIÓN
      </label>
      <input
        id={`${idPrefix}-description`}
        type="text"
        placeholder="Descripción..."
        value={searchInputValues.description ?? variables.description ?? ''}
        onChange={(e) => onPanelFieldChange('description', e.target.value)}
      />
    </div>
  </>
);

const DocumentsSearchPanel = ({ idPrefix, searchInputValues, variables, onPanelFieldChange }) => (
  <>
    <div className="module-search-bar-panel__row module-search-bar-panel__row--2">
      <div className="module-search-bar-field">
        <label className="module-search-bar-label" htmlFor={`${idPrefix}-amount-start`}>
          MONTO DESDE
        </label>
        <div className="module-search-bar-amount-wrap">
          <span className="module-search-bar-amount-prefix" aria-hidden="true">$</span>
          <input
            id={`${idPrefix}-amount-start`}
            type="text"
            placeholder="Monto mínimo..."
            inputMode="decimal"
            value={searchInputValues.amountStart ?? variables.amountStart ?? ''}
            onChange={(e) => onPanelFieldChange('amountStart', sanitizeFilterAmountInput(e.target.value))}
          />
        </div>
      </div>
      <div className="module-search-bar-field">
        <label className="module-search-bar-label" htmlFor={`${idPrefix}-amount-end`}>
          MONTO HASTA
        </label>
        <div className="module-search-bar-amount-wrap">
          <span className="module-search-bar-amount-prefix" aria-hidden="true">$</span>
          <input
            id={`${idPrefix}-amount-end`}
            type="text"
            placeholder="Monto máximo..."
            inputMode="decimal"
            value={searchInputValues.amountEnd ?? variables.amountEnd ?? ''}
            onChange={(e) => onPanelFieldChange('amountEnd', sanitizeFilterAmountInput(e.target.value))}
          />
        </div>
      </div>
    </div>
    <div className="module-search-bar-panel__row module-search-bar-panel__row--2">
      <div className="module-search-bar-field">
        <label className="module-search-bar-label" htmlFor={`${idPrefix}-rut`}>RUT</label>
        <input
          id={`${idPrefix}-rut`}
          type="text"
          placeholder="RUT..."
          value={
            searchInputValues.rut !== undefined
              ? formatRut(searchInputValues.rut)
              : formatRut(variables.rut) || ''
          }
          onChange={(e) => onPanelFieldChange('rut', e.target.value.replace(/[^0-9Kk]/g, ''))}
        />
      </div>
      <div className="module-search-bar-field">
        <label className="module-search-bar-label" htmlFor={`${idPrefix}-name`}>
          RAZÓN SOCIAL
        </label>
        <input
          id={`${idPrefix}-name`}
          type="text"
          placeholder="Razón Social..."
          value={searchInputValues.name ?? variables.name ?? ''}
          onChange={(e) => onPanelFieldChange('name', e.target.value)}
        />
      </div>
    </div>
    <div className="module-search-bar-field">
      <label
        className="module-search-bar-label"
        htmlFor={`${idPrefix}-doc-num`}
        title="Puedes buscar por múltiples folios separando estos por ; ejemplo: 1234; 7654"
      >
        FOLIO / N° DOCUMENTO
        <span className="module-search-bar-label-hint" aria-hidden="true">
          ⓘ
        </span>
      </label>
      <input
        id={`${idPrefix}-doc-num`}
        type="text"
        placeholder="ej: 1786; 2001; 3005"
        value={searchInputValues.docNum ?? variables.docNum ?? ''}
        onChange={(e) => onPanelFieldChange('docNum', e.target.value)}
      />
    </div>
  </>
);

const AsientosSearchPanel = ({
  idPrefix,
  searchInputValues,
  variables,
  onPanelFieldChange,
  panelAmountValue,
}) => (
  <>
    <div className="module-search-bar-field">
      <label className="module-search-bar-label" htmlFor={`${idPrefix}-entry-number`}>
        N° ASIENTO
      </label>
      <input
        id={`${idPrefix}-entry-number`}
        type="text"
        inputMode="numeric"
        placeholder="Número exacto..."
        value={searchInputValues.entryNumber ?? (variables.entryNumber != null ? String(variables.entryNumber) : '')}
        onChange={(e) => onPanelFieldChange('entryNumber', e.target.value.replace(/[^\d]/g, ''))}
      />
    </div>
    <div className="module-search-bar-panel__row module-search-bar-panel__row--2">
      <div className="module-search-bar-field">
        <label className="module-search-bar-label" htmlFor={`${idPrefix}-min-amount`}>
          MONTO DESDE
        </label>
        <div className="module-search-bar-amount-wrap">
          <span className="module-search-bar-amount-prefix" aria-hidden="true">$</span>
          <input
            id={`${idPrefix}-min-amount`}
            type="text"
            placeholder="Monto mínimo..."
            inputMode="decimal"
            value={panelAmountValue('minAmount')}
            onChange={(e) => onPanelFieldChange('minAmount', sanitizeFilterAmountInput(e.target.value))}
          />
        </div>
      </div>
      <div className="module-search-bar-field">
        <label className="module-search-bar-label" htmlFor={`${idPrefix}-max-amount`}>
          MONTO HASTA
        </label>
        <div className="module-search-bar-amount-wrap">
          <span className="module-search-bar-amount-prefix" aria-hidden="true">$</span>
          <input
            id={`${idPrefix}-max-amount`}
            type="text"
            placeholder="Monto máximo..."
            inputMode="decimal"
            value={panelAmountValue('maxAmount')}
            onChange={(e) => onPanelFieldChange('maxAmount', sanitizeFilterAmountInput(e.target.value))}
          />
        </div>
      </div>
    </div>
    <div className="module-search-bar-field">
      <label className="module-search-bar-label" htmlFor={`${idPrefix}-account`}>
        CUENTA
      </label>
      <input
        id={`${idPrefix}-account`}
        type="text"
        placeholder="Código o nombre..."
        value={searchInputValues.accountQuery ?? variables.accountQuery ?? ''}
        onChange={(e) => onPanelFieldChange('accountQuery', e.target.value)}
      />
    </div>
    <div className="module-search-bar-field">
      <label className="module-search-bar-label" htmlFor={`${idPrefix}-description`}>
        DESCRIPCIÓN / GLOSA
      </label>
      <input
        id={`${idPrefix}-description`}
        type="text"
        placeholder="Glosa o nombre de cuenta..."
        value={searchInputValues.description ?? variables.description ?? ''}
        onChange={(e) => onPanelFieldChange('description', e.target.value)}
      />
    </div>
  </>
);

const ModuleSearchBar = ({ module, fetching = false }) => {
  const config = getModuleSearchConfig(module);
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const debounceRef = useRef(null);
  const variablesRef = useRef({});

  const idPrefix = `module-search-${module}`;
  const panelId = `module-search-bar-panel-${module}`;

  const [panelOpen, setPanelOpen] = useState(false);
  const [mainQuery, setMainQuery] = useState('');
  const [searchingDots, setSearchingDots] = useState('.');
  const [variables, setVariables] = useState({});
  const [searchInputValues, setSearchInputValues] = useState(() => config.emptyPanelValues());

  const allFilters = useSelector(config.selectFilters);

  useEffect(() => {
    variablesRef.current = variables;
  }, [variables]);

  useEffect(() => {
    if (!allFilters) return;
    setVariables(allFilters);
    setSearchInputValues(config.panelValuesFromVariables(allFilters));
    setMainQuery(config.mainQueryFromVariables(allFilters));
  }, [allFilters, module, config]);

  const commitFilters = useCallback(
    (newVariables, closePanel = false) => {
      setVariables(newVariables);
      const newSearchParams = new URLSearchParams(location.search);
      syncModuleSearchFieldsToUrl(newSearchParams, newVariables, config.searchKeys, {
        pageParam: config.pageParam,
        clearParams: config.clearParams,
      });
      history.push({ search: newSearchParams.toString() });
      dispatch(config.dispatchFilters(newVariables));
      config.updateEnhancedIcons(dispatch, newVariables);
      if (closePanel) setPanelOpen(false);
    },
    [config, dispatch, history, location.search],
  );

  const applyMainQuery = useCallback(
    (query) => {
      const inferred = config.inferSearchFilters(query);
      const newVariables = mergeInferredIntoVariables(
        variablesRef.current,
        inferred,
        config.searchKeys,
      );
      setSearchInputValues(config.panelValuesFromVariables(newVariables));
      commitFilters(newVariables);
    },
    [commitFilters, config],
  );

  const handlePanelApply = useCallback(() => {
    const newVariables = buildVariablesFromPanelInput(
      variables,
      searchInputValues,
      config.searchKeys,
      config.parseFilter,
    );
    setMainQuery(config.mainQueryFromVariables(newVariables));
    commitFilters(newVariables, true);
  }, [commitFilters, config, searchInputValues, variables]);

  const handleClear = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = null;
    const newVariables = stripModuleSearchFields(variables, config.searchKeys);
    setMainQuery('');
    setSearchInputValues(config.emptyPanelValues());
    commitFilters(newVariables, true);
  }, [commitFilters, config, variables]);

  const handleMainChange = (value) => {
    setMainQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      applyMainQuery(value);
    }, DEBOUNCE_MS);
  };

  const handleMainKeyDown = (e) => {
    if (e.key === 'Escape' && panelOpen) {
      e.preventDefault();
      setPanelOpen(false);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = null;
      if (panelOpen) {
        handlePanelApply();
      } else {
        applyMainQuery(mainQuery);
      }
    }
  };

  const handlePanelKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setPanelOpen(false);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      handlePanelApply();
    }
  };

  const onPanelFieldChange = (name, value) => {
    setSearchInputValues((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (!panelOpen) return undefined;
    const handleClickOutside = (event) => {
      if (!anchorRef.current?.contains(event.target)) setPanelOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setPanelOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [panelOpen]);

  const searchActive = hasModuleSearchFilter(variables, config.searchKeys);
  const showClearButton = Boolean(mainQuery.trim()) || searchActive;
  // "Buscando" only after commit (debounce ended); fetching stays false while typing.
  const showSearchingMessage = fetching && (Boolean(mainQuery.trim()) || searchActive);

  useEffect(() => {
    if (!showSearchingMessage) return undefined;
    setSearchingDots('.');
    const intervalId = setInterval(() => {
      setSearchingDots((prev) => (prev.length >= 3 ? '.' : `${prev}.`));
    }, 400);
    return () => clearInterval(intervalId);
  }, [showSearchingMessage]);

  const panelAmountValue = (key) => {
    if (searchInputValues[key] != null && searchInputValues[key] !== '') {
      return searchInputValues[key];
    }
    return variables[key] != null ? String(variables[key]) : '';
  };

  return (
    <div className="module-search-bar-anchor" ref={anchorRef}>
      <div className="module-search-bar-input-row scfo-modal-form-body">
        <div className="module-search-bar-input-wrap">
          <input
            type="text"
            placeholder={config.placeholder || 'Buscar...'}
            value={mainQuery}
            onChange={(e) => handleMainChange(e.target.value)}
            onKeyDown={handleMainKeyDown}
            aria-label={config.ariaLabel}
            aria-expanded={panelOpen}
            aria-controls={panelId}
            className={showClearButton ? 'module-search-bar-input--with-clear' : ''}
          />
          {showClearButton ? (
            <button
              type="button"
              className="module-search-bar-clear"
              onClick={handleClear}
              aria-label="Limpiar búsqueda"
            >
              <FeatherIcon icon="x" size={16} />
            </button>
          ) : null}
        </div>
        <button
          type="button"
          className={`module-search-bar-toggle ${panelOpen ? 'module-search-bar-toggle--open' : ''} ${searchActive ? 'module-search-bar-toggle--active' : ''}`}
          onClick={() => setPanelOpen((o) => !o)}
          aria-expanded={panelOpen}
          aria-label="Filtros de búsqueda"
        >
          <FeatherIcon icon="chevron-down" size={18} className="module-search-bar-toggle__chevron" />
        </button>
      </div>

      {showSearchingMessage ? (
        <span className="module-search-bar-searching" aria-live="polite">
          Buscando{searchingDots}
        </span>
      ) : null}

      <div
        id={panelId}
        ref={panelRef}
        role="dialog"
        aria-hidden={!panelOpen}
        className={`module-search-bar-panel ${module === 'documents' ? 'module-search-bar-panel--documents' : ''} ${module === 'asientos' ? 'module-search-bar-panel--asientos' : ''} ${panelOpen ? 'module-search-bar-panel--expanded' : ''}`}
        onKeyDown={handlePanelKeyDown}
      >
        <div className="module-search-bar-panel-inner scfo-modal-form-body">
          {module === 'transactions' ? (
            <TransactionsSearchPanel
              idPrefix={idPrefix}
              searchInputValues={searchInputValues}
              variables={variables}
              onPanelFieldChange={onPanelFieldChange}
              panelAmountValue={panelAmountValue}
            />
          ) : module === 'asientos' ? (
            <AsientosSearchPanel
              idPrefix={idPrefix}
              searchInputValues={searchInputValues}
              variables={variables}
              onPanelFieldChange={onPanelFieldChange}
              panelAmountValue={panelAmountValue}
            />
          ) : (
            <DocumentsSearchPanel
              idPrefix={idPrefix}
              searchInputValues={searchInputValues}
              variables={variables}
              onPanelFieldChange={onPanelFieldChange}
            />
          )}
          <div className="module-search-bar-panel__actions">
            <ScfoButton type="button" variant="ghost" onClick={handleClear}>
              Limpiar
            </ScfoButton>
            <ScfoButton type="button" variant="primary" onClick={handlePanelApply}>
              Aplicar
            </ScfoButton>
          </div>
        </div>
      </div>
    </div>
  );
};

ModuleSearchBar.propTypes = {
  module: PropTypes.oneOf(['transactions', 'documents', 'asientos']).isRequired,
  fetching: PropTypes.bool,
};

export default ModuleSearchBar;
